import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Plus, Warning, TrendUp, FileText } from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

function NewInvoice({ onSave, onClose, students }) {
  const { t } = useTranslation();
  const [f, setF] = useState({
    student_id: "", description: "", qty: 1, unit_price: 0,
    discount: 0, scholarship: 0, tax: 0, installments_count: 1,
    due_date: new Date().toISOString().slice(0, 10), notes: "",
  });
  const [saving, setSaving] = useState(false);
  const subtotal = Number(f.qty) * Number(f.unit_price);
  const total = Math.max(0, subtotal - Number(f.discount) - Number(f.scholarship) + Number(f.tax));

  const submit = async () => {
    if (!f.student_id || !f.description || !f.unit_price) return toast.error(t("payments.toasts.fillFields"));
    setSaving(true);
    try {
      const { data } = await api.post("/invoices", {
        student_id: f.student_id,
        items: [{ description: f.description, qty: Number(f.qty), unit_price: Number(f.unit_price) }],
        discount: Number(f.discount), scholarship: Number(f.scholarship), tax: Number(f.tax),
        installments_count: Number(f.installments_count),
        due_date: f.due_date, notes: f.notes,
      });
      toast.success(t("payments.toasts.invoiceCreated", { no: data.invoice_no })); onSave(); onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("payments.fields.student")}</Label>
        <Select value={f.student_id} onValueChange={(v) => setF({ ...f, student_id: v })}>
          <SelectTrigger data-testid="invoice-student-select" className="mt-1.5 h-10"><SelectValue placeholder={t("payments.fields.selectStudent")} /></SelectTrigger>
          <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} ({s.student_number})</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("payments.fields.description")}</Label>
        <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder={t("payments.fields.descriptionPh")} className="mt-1.5 h-10" />
      </div>
      {[
        [t("payments.fields.qty"), "qty"], [t("payments.fields.unitPrice"), "unit_price"], [t("payments.fields.discount"), "discount"],
        [t("payments.fields.scholarship"), "scholarship"], [t("payments.fields.tax"), "tax"], [t("payments.fields.installments"), "installments_count"],
      ].map(([lbl, k]) => (
        <div key={k}>
          <Label className="text-xs uppercase tracking-wider font-semibold">{lbl}</Label>
          <Input type="number" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1.5 h-10" />
        </div>
      ))}
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("payments.fields.dueDate")}</Label>
        <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="mt-1.5 h-10" />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("payments.fields.notes")}</Label>
        <Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2} className="mt-1.5" />
      </div>
      <div className="md:col-span-2 p-3 rounded-md bg-secondary/40 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("payments.summary.total")}</div>
          <div className="text-2xl font-bold font-mono">${total.toFixed(2)}</div>
        </div>
        <div className="text-xs text-muted-foreground text-right">
          {t("payments.summary.subtotal", { amount: `$${subtotal.toFixed(2)}` })}<br />
          {f.installments_count > 1 && t("payments.summary.installmentsOf", { count: f.installments_count, amount: `$${(total / Number(f.installments_count)).toFixed(2)}` })}
        </div>
      </div>
      <DialogFooter className="md:col-span-2">
        <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
        <Button onClick={submit} disabled={saving} data-testid="invoice-save">
          {saving ? t("common.creating") : t("payments.createInvoice")}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function Payments() {
  const { t } = useTranslation();
  const [tab, setTab] = useState("invoices");
  const [invoices, setInvoices] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [students, setStudents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [chart, setChart] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [open, setOpen] = useState(false);
  const [payDialog, setPayDialog] = useState(null);
  const [studentFilter, setStudentFilter] = useState("all");

  const load = async () => {
    try {
      const [i, r, s, sum, ch, od] = await Promise.all([
        api.get("/invoices"), api.get("/receipts"), api.get("/students"),
        api.get("/finance/summary"), api.get("/finance/revenue-chart"), api.get("/finance/overdue"),
      ]);
      setInvoices(i.data); setReceipts(r.data); setStudents(s.data); setSummary(sum.data);
      setChart(ch.data); setOverdue(od.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const studentName = (id) => students.find((s) => s.id === id)?.full_name || (id || "").slice(0, 8);

  const pay = async (inv, amount, method) => {
    try {
      await api.post(`/invoices/${inv.id}/pay`, { amount, method });
      toast.success(t("payments.toasts.paymentRecorded"));
      setPayDialog(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filteredInvoices = useMemo(
    () => studentFilter === "all" ? invoices : invoices.filter((i) => i.student_id === studentFilter),
    [invoices, studentFilter],
  );
  const filteredReceipts = useMemo(
    () => studentFilter === "all" ? receipts : receipts.filter((r) => r.student_id === studentFilter),
    [receipts, studentFilter],
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{t("nav.payments")}</div>
          <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>{t("payments.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("payments.subtitle")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-invoice-btn" className="rounded-full gap-2"><Plus size={16} weight="bold" /> {t("payments.newInvoice")}</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle style={{ fontFamily: "Manrope" }}>{t("payments.createInvoice")}</DialogTitle></DialogHeader>
            <NewInvoice students={students} onSave={load} onClose={() => setOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: t("payments.summary.outstanding"), value: `$${(summary?.outstanding || 0).toFixed(0)}`, icon: Warning, tone: "bg-destructive/10 text-destructive" },
          { label: t("payments.summary.collected"), value: `$${(summary?.collected_this_month || 0).toFixed(0)}`, icon: TrendUp, tone: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
          { label: t("payments.summary.invoices"), value: summary?.total_invoices || 0, icon: FileText, tone: "bg-primary/10 text-primary" },
          { label: t("payments.summary.overdue"), value: summary?.overdue_count || 0, icon: Warning, tone: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
        ].map((k) => (
          <Card key={k.label} className="border-border/60"><CardContent className="p-5">
            <div className={cn("w-10 h-10 rounded-md flex items-center justify-center", k.tone)}>
              <k.icon size={20} weight="duotone" />
            </div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mt-4">{k.label}</div>
            <div className="text-3xl font-bold font-mono mt-1">{k.value}</div>
          </CardContent></Card>
        ))}
      </div>

      {/* Overdue banner */}
      {overdue.length > 0 && (
        <Card className="border-destructive bg-destructive/5" data-testid="overdue-banner">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-destructive/10 text-destructive flex items-center justify-center shrink-0">
                <Warning size={20} weight="duotone" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{t("payments.overdueBanner.title", { count: overdue.length })}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{t("payments.overdueBanner.subtitle")}</div>
                <div className="mt-2 flex gap-2 flex-wrap">
                  {overdue.slice(0, 4).map((o) => (
                    <button
                      key={o.id}
                      onClick={() => { setStudentFilter(o.student_id); setTab("invoices"); }}
                      className="text-[10px] font-mono px-2 py-1 rounded bg-background border border-destructive/40 hover:bg-destructive/10 transition-colors"
                      data-testid={`overdue-chip-${o.id}`}
                    >
                      {o.invoice_no} · {o.student_name} · ${o.total?.toFixed(0)}
                    </button>
                  ))}
                  {overdue.length > 4 && <span className="text-[10px] font-mono px-2 py-1 text-muted-foreground">+{overdue.length - 4} more</span>}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Revenue trend chart */}
      <Card className="border-border/60"><CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{t("payments.chart.header")}</div>
            <h3 className="text-lg font-semibold mt-0.5" style={{ fontFamily: "Manrope" }}>{t("payments.chart.title")}</h3>
          </div>
          <Badge variant="outline" className="gap-1 text-[10px]"><TrendUp size={12} weight="bold" /> {t("payments.chart.last12Months")}</Badge>
        </div>
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer>
            <AreaChart data={chart} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="collectedFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="invoicedFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0.2} />
                  <stop offset="100%" stopColor="hsl(var(--muted-foreground))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 12 }}
                formatter={(v) => `$${(v || 0).toLocaleString()}`}
              />
              <Area type="monotone" dataKey="invoiced" stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} fill="url(#invoicedFill)" name={t("payments.chart.invoiced")} />
              <Area type="monotone" dataKey="collected" stroke="hsl(var(--primary))" strokeWidth={2} fill="url(#collectedFill)" name={t("payments.chart.collected")} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent></Card>

      {/* Student filter */}
      <div className="flex items-center gap-3 flex-wrap">
        <Label className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">{t("payments.filter.byStudent")}</Label>
        <Select value={studentFilter} onValueChange={setStudentFilter}>
          <SelectTrigger className="w-72 h-9" data-testid="payment-student-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")}</SelectItem>
            {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} ({s.student_number})</SelectItem>)}
          </SelectContent>
        </Select>
        {studentFilter !== "all" && (
          <Button size="sm" variant="ghost" onClick={() => setStudentFilter("all")} data-testid="clear-filter-btn">
            {t("common.reset")}
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="invoices" data-testid="tab-invoices">{t("payments.tabs.invoices")}</TabsTrigger>
          <TabsTrigger value="receipts" data-testid="tab-receipts">{t("payments.tabs.receipts")}</TabsTrigger>
        </TabsList>

        <TabsContent value="invoices" className="mt-4">
          <Card className="border-border/60 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="invoices-table">
                <thead className="bg-secondary/40 border-b border-border">
                  <tr className="text-left">
                    {[
                      t("payments.invoicesTable.invoiceNo"),
                      t("payments.invoicesTable.student"),
                      t("payments.invoicesTable.total"),
                      t("payments.invoicesTable.installments"),
                      t("payments.invoicesTable.due"),
                      t("payments.invoicesTable.status"),
                      "",
                    ].map((h, i) => <th key={i} className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-muted-foreground">{t("common.noData")}</td></tr>
                  )}
                  {filteredInvoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-border last:border-0 hover:bg-secondary/40">
                      <td className="px-4 py-3 font-mono text-xs">{inv.invoice_no}</td>
                      <td className="px-4 py-3 font-semibold">{studentName(inv.student_id)}</td>
                      <td className="px-4 py-3 font-mono font-bold">${inv.total?.toFixed(0)}</td>
                      <td className="px-4 py-3 text-xs">{inv.installments_paid}/{inv.installments_count}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{inv.due_date}</td>
                      <td className="px-4 py-3">
                        <span className={cn(
                          "text-[10px] font-bold px-2 py-1 rounded uppercase",
                          inv.status === "paid" ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" :
                            inv.status === "overdue" ? "bg-destructive/10 text-destructive" :
                            inv.status === "partial" ? "bg-sky-500/10 text-sky-600 dark:text-sky-400" :
                            "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                        )}>{t(`payments.status.${inv.status}`, { defaultValue: inv.status })}</span>
                      </td>
                      <td className="px-4 py-3">
                        {inv.status !== "paid" && (
                          <Button size="sm" variant="outline" onClick={() => setPayDialog(inv)} data-testid={`pay-btn-${inv.id}`}>
                            {t("payments.invoicesTable.recordPayment")}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="receipts" className="mt-4">
          <Card className="border-border/60 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="receipts-table">
                <thead className="bg-secondary/40 border-b border-border">
                  <tr className="text-left">
                    {[
                      t("payments.receiptsTable.receiptNo"),
                      t("payments.receiptsTable.student"),
                      t("payments.receiptsTable.amount"),
                      t("payments.receiptsTable.method"),
                      t("payments.receiptsTable.installment"),
                      t("payments.receiptsTable.paid"),
                    ].map((h, i) => <th key={i} className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {filteredReceipts.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-sm text-muted-foreground">{t("common.noData")}</td></tr>
                  )}
                  {filteredReceipts.map((r) => (
                    <tr key={r.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-3 font-mono text-xs">{r.receipt_no}</td>
                      <td className="px-4 py-3 font-semibold">{studentName(r.student_id)}</td>
                      <td className="px-4 py-3 font-mono font-bold">${r.amount?.toFixed(0)}</td>
                      <td className="px-4 py-3 text-xs">{r.method}</td>
                      <td className="px-4 py-3 text-xs">#{r.installment_no}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{r.paid_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {payDialog && (
        <Dialog open onOpenChange={() => setPayDialog(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("payments.pay.title", { invoice: payDialog.invoice_no })}</DialogTitle></DialogHeader>
            <PayForm inv={payDialog} onPay={pay} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function PayForm({ inv, onPay }) {
  const { t } = useTranslation();
  const remaining = inv.installments_count - inv.installments_paid;
  const perInstall = inv.total / inv.installments_count;
  const [amount, setAmount] = useState(perInstall.toFixed(2));
  const [method, setMethod] = useState("Credit Card");
  const METHODS = [
    { value: "Credit Card", labelKey: "credit_card" },
    { value: "Bank Transfer", labelKey: "bank_transfer" },
    { value: "Cash", labelKey: "cash" },
    { value: "Check", labelKey: "check" },
  ];
  return (
    <div className="space-y-4">
      <div className="p-3 rounded-md bg-secondary/40 text-sm">
        {t("payments.pay.remaining", { amount: `$${(perInstall * remaining).toFixed(2)}`, count: remaining })}
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("payments.fields.amount")}</Label>
        <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 h-10" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("payments.fields.method")}</Label>
        <Select value={method} onValueChange={setMethod}>
          <SelectTrigger className="mt-1.5 h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            {METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{t(`payments.methods.${m.labelKey}`)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <DialogFooter>
        <Button onClick={() => onPay(inv, Number(amount), method)} data-testid="confirm-pay-btn">{t("payments.pay.recordBtn")}</Button>
      </DialogFooter>
    </div>
  );
}
